export const GENERIC_RENDITION = 'generic_rendition';

export enum SortOrder {
    asc = 'asc',
    desc = 'desc'
}

export type AssetId = string;
export type SiteKey = string;
export type ChannelId = string;
export type PlayerId = string;
export type RequestId = string;
export type IFrameContent = string;

export interface StatusOutput {
    status: string;
}

export interface PublishStatusOutput {
    status: 'PUBLISHED' | 'NOT_PUBLISHED';
    message?: string;
}

export interface PlayersMap {
    [key: PlayerId]: IFrameContent;
}

export type Schema = keyof EntityMetadata<unknown>;
export type Adapter = 'CmsMetadataAdapter';
export type MetadataAdapter = 'taxonomiesAsTerms' | 'taxonomiesAsIds';
export type Timestamp = string; // ISO 8601
export type DateTime = string; // YYYY-MM-DD hh:mm; no seconds, implicit UTC

export type SchemaList =
      Schema
    | `${Schema},${Schema}`
    | `${Schema},${Schema},${Schema}`;

export type Sort = `${SortProperties} ${SortOrder}`;

export interface GetEntitiesParameters {
    type?: EntityType;
    subtype?: EntitySubtype;
    q?: string;
    properties?: string;
    fields?: string;
    start?: number;
    limit?: number;
    sort?: Sort;
    renditions?: string;
    adapter?: Adapter;
    schema?: SchemaList;
    ignore_missing_schema?: boolean;
}

export interface GetEntityParameters {
    language?: string;
    schema?: SchemaList;
    ignore_missing_schema?: boolean;
    metadata_adapters?: MetadataAdapter;
    adapter?: Adapter;
    fields?: string;
    properties?: string;
}

export interface GetEntitiesOutput {
    resultCount?: number;
    entities?: Entity[];
}

export interface GetMinisitesOutput {
    api_key: string;
    folder_id: AssetId;
    channel_id: ChannelId;
}

export interface SiteSettingsDam {
    folder_id: AssetId;
    channel_id: ChannelId;
    minisite_id: AssetId,
    investor_id?: AssetId;
    careers_id?: AssetId;
    shared_id?: AssetId | boolean;
}

export interface EntityOutput {
    request_id?: RequestId;
    entity_id?: AssetId;
}

export interface Entity {
    properties: EntityProperties;
    metadata: EntityMetadata<{[key: string]: string}>;
    renditions: { [key in EntityRendition]: string };
}

export interface Selecting {
    properties: (keyof EntityProperties)[];
    renditions: Rendition[];
    schema: Schema[];
    fields: (
        | keyof DefaultSchema<unknown>
        | keyof ReplycomSchema<unknown>
        | keyof CommonSchema
    )[];
}

export type SortProperties =
    | 'id'
    | 'type'
    | 'sub_type'
    | 'date_created'
    | 'created_by'
    | 'last_date_updated'
    | 'last_updated_by'
    | 'timestamp';

export interface EntityProperties {
    _id: AssetId;
    _type: EntityType;
    _subType?: EntitySubtype;
    _dateCreated?: Timestamp;
    _createdBy?: string;
    _lastDateUpdated?: Timestamp;
    _lastUpdatedBy?: string;
    _timestamp?: Timestamp;
    _durationMilliseconds?: number;
    _parentId?: string;
    _parent_global_asset_id?: string;
}

export interface EntityPropertiesInput {
    _id: AssetId;
    _file_name: string;
    _type: EntityType;
    _subtype?: EntitySubtype;
    _parent_global_asset_id: string;
}

export interface GetRenditionsOutput {
    renditions?: EntityRenditionsOutput;
}

export type EntityRenditionsOutput = {
    [key in EntityRendition]: EntityRenditionAttributes
};

export interface EntityRenditionAttributes {
    protocols: {
        http: string,
        path: string
    };
    contentType: string;
    fileSizeBytes: number;
    fileName: string;
    isAvailable: boolean;
    isEncoding: boolean;
    isArchived: boolean;
    hasExifMetadata: boolean;
    bitrateBps?: string;
    widthPx: string;
    heightPx: string;
    label?: string;
    url?: string;
}

export interface ChannelPublishRequestBody {
    globalChannelId: ChannelId;
}

export type EntityType =
    | 'Audio' //media
    | 'Binary' //media
    | 'Document' //media
    | 'Folder' 
    | 'Image' //media
    | 'Player'
    | 'Slideshow' //media
    | 'Video' //media
    | 'BusinessEntity' //minisite
    ;

export type EntitySubtype =
    | 'StandardImage'
    | 'StandardAudio'
    | 'Vod'
    | 'Minisite' //minisite
    | 'Channel' //minisite
    ;


export enum Rendition {
    Essence = 'role:Essence',
    Thumbnail = 'role:Thumbnail',
    Cover = 'role:Cover',
    Proxy = 'role:Proxy',
    Hero = 'label:Hero',
    inline = 'label:inline'
}

export enum EntityRendition {
    Essence = 'role_Essence',
    Thumbnail = 'role_Thumbnail',
    Cover = 'role_Cover',
    Proxy = 'role_Proxy',
    Hero = 'label_Hero',
    inline = 'label_inline',
    _unknown = '_unknown'
}

export type MediaRole =
    | 'Essence'
    | 'Proxy'
    | 'Thumbnail'
    | 'Cover'
    | 'Hero'
    | 'inline'
    | 'generic_rendition'
    ;

export type ReplycomMetadataFlatKeys = `replycom.${keyof ReplycomSchema<unknown>}`;
export type CommonMetadataFlatKeys = `common.${keyof CommonSchema}`;
export type DefaultMetadataFlatKeys = `${keyof DefaultSchema<unknown>}`;

export interface CreateEntityRequestBody {
    properties: EntityPropertiesInput;
    metadata:
        | FlatMetadata<DefaultSchema<string>>
        | FlatMetadata<ReplycomSchema<string>>
        | FlatMetadata<CommonSchema>
        ;
    metadata_adapters: MetadataAdapter;
    url: string;
}

export interface PatchEntityRequestBody {
    metadata:
        | FlatMetadata<DefaultSchema<string>>
        | FlatMetadata<ReplycomSchema<string>>
        ;
    metadata_adapters: MetadataAdapter;
}

export interface EntityMetadata<Taxonomy> {
    common?: CommonSchema;
    replycom?: ReplycomSchema<Taxonomy>;
    default?: DefaultSchema<Taxonomy>;
}

export interface CommonSchema {
    master_title: string;
}

export interface ReplycomSchema<Taxonomy> {
    replycom_title?: string;
    replycom_locale_title?: string;
    replycom_brief?: string;
    replycom_start_datetime?: DateTime;
    replycom_language?: Taxonomy;
    replycom_keywords?: Taxonomy;
    replycom_topic?: Taxonomy;
    replycom_speakers?: Taxonomy;
    replycom_industries?: Taxonomy;
    replycom_companies?: Taxonomy;
    replycom_customers?: Taxonomy;
    replycom_context_keywords?: Taxonomy;
}

export interface DefaultSchema<Taxonomy> {
    title?: string;
    keywords?: Taxonomy;
    topics?: Taxonomy;
    customers?: Taxonomy;
    industries?: Taxonomy;
    context_keywords?: Taxonomy;
    smart_keywords?: Taxonomy; // Standard Image
    speakers?: Taxonomy; // Standard Audio
    language?: Taxonomy; // Document and Binary
    start_datetime?: DateTime; // Document and Binary
}

export type FlatMetadata<S extends CommonSchema | ReplycomSchema<any> | DefaultSchema<any>> =
    S extends CommonSchema ? { [key in CommonMetadataFlatKeys]?: string }
        : S extends ReplycomSchema<any> ? { [key in ReplycomMetadataFlatKeys]?: string }
            : S extends DefaultSchema<any> ? { [key in DefaultMetadataFlatKeys]?: string }
                : Record<string, never>;

export type MetadataPrefix<S extends CommonSchema | ReplycomSchema<any> | DefaultSchema<any>> =
    S extends CommonSchema ? 'common'
        : S extends ReplycomSchema<any> ? 'replycom'
            : S extends DefaultSchema<any> ? ''
                : never;

export enum AssetType {
    STANDARD_AUDIO = 'STANDARD_AUDIO',
    BINARY = 'BINARY',
    DOCUMENT = 'DOCUMENT',
    STANDARD_IMAGE = 'STANDARD_IMAGE',
    SLIDESHOW = 'SLIDESHOW',
    VOD = 'VOD'
}

export const ASSET_TYPES = new Map<AssetType, [EntityType, EntitySubtype?]>([
    [AssetType.STANDARD_IMAGE, ['Image', 'StandardImage']], // image/
    [AssetType.DOCUMENT, ['Document']], // application/pdf
    [AssetType.STANDARD_AUDIO, ['Audio', 'StandardAudio']], // audio/
    [AssetType.VOD, ['Video', 'Vod']], // video/
    [AssetType.SLIDESHOW, ['Slideshow']], // ppt(x)
    [AssetType.BINARY, ['Binary']] // others
]);

export const MIME_TYPES = new Map<string, AssetType>([
    ['audio/', AssetType.STANDARD_AUDIO],
    ['application/pdf', AssetType.DOCUMENT],
    ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', AssetType.DOCUMENT],
    ['image/', AssetType.STANDARD_IMAGE],
    ['video/', AssetType.VOD],
    ['application/vnd.openxmlformats-officedocument.presentationml.presentation', AssetType.SLIDESHOW],
    ['application/vnd.ms-powerpoint', AssetType.SLIDESHOW]
]);

export interface Filter {
    id: string;
    value: string;
    type: 'category';
    taxonomy?: string;
    fields: string[];
}

export const FILTERS: Filter[] = [
    {
        id: 'category:type',
        value: 'type',
        type: 'category',
        taxonomy: 'Types',
        fields: [
            '_type_subtype'
        ]
    },
    {
        id: 'category:folder',
        value: 'folder',
        type: 'category',
        taxonomy: 'Folders',
        fields: [
            'parent_global_asset_id_str'
        ]
    },
    {
        id: 'category:title',
        value: 'title',
        type: 'category',
        fields: [
            'title_str',
            'replycom_replycom_title_str',
            'common_master_title_str'
        ]
    },
    {
        id: 'category:keywords',
        value: 'keywords',
        type: 'category',
        taxonomy: 'Keywords',
        fields: [
            'taxonomy_keywords',
            'taxonomy_replycom_keywords'
        ]
    },
    {
        id: 'category:topics_static',
        value: 'topics_static',
        type: 'category',
        taxonomy: 'Topics_Static',
        fields: [
            'taxonomy_topic'
        ]
    },
    {
        id: 'category:topic',
        value: 'topic',
        type: 'category',
        taxonomy: 'Topic',
        fields: [
            'taxonomy_replycom_topic'
        ]
    },
    {
        id: 'category:customers',
        value: 'customers',
        type: 'category',
        taxonomy: 'Customers',
        fields: [
            'taxonomy_customers',
            'taxonomy_replycom_customers'
        ]
    },
    {
        id: 'category:industries',
        value: 'industries',
        type: 'category',
        taxonomy: 'Industries',
        fields: [
            'taxonomy_industries',
            'taxonomy_replycom_industries'
        ]
    },
    {
        id: 'category:context_keywords',
        value: 'context_keywords',
        type: 'category',
        taxonomy: 'Context Keywords',
        fields: [
            'taxonomy_context_keywords',
            'taxonomy_replycom_context_keywords'
        ]
    },
    {
        id: 'category:smart_keywords',
        value: 'smart_keywords',
        type: 'category',
        taxonomy: 'Smart Keywords',
        fields: [
            'taxonomy_smart_keywords'
        ]
    },
    {
        id: 'category:speakers',
        value: 'speakers',
        type: 'category',
        taxonomy: 'Speakers',
        fields: [
            'taxonomy_speakers',
            'taxonomy_replycom_speakers'
        ]
    },
    {
        id: 'category:language',
        value: 'language',
        type: 'category',
        taxonomy: 'Languages',
        fields: [
            'taxonomy_language',
            'taxonomy_replycom_language'
        ]
    }
];

export function convertMetadata<T, S>(metadata: EntityMetadata<T>, convert: (t: T) => S): EntityMetadata<S> {
    if (!metadata) {
        return {};
    }
    const d = metadata.default ?? {};
    const r = metadata.replycom ?? {};
    return {
        common: metadata.common,
        default: {
            title: d.title,
            context_keywords: convert(d.context_keywords),
            customers: convert(d.customers),
            industries: convert(d.industries),
            keywords: convert(d.keywords),
            smart_keywords: convert(d.smart_keywords),
            speakers: convert(d.speakers),
            topics: convert(d.topics),
            language: convert(d.language),
            start_datetime: d.start_datetime
        },
        replycom: {
            replycom_brief: r.replycom_brief,
            replycom_companies: convert(r.replycom_companies),
            replycom_customers: convert(r.replycom_customers),
            replycom_industries: convert(r.replycom_industries),
            replycom_keywords: convert(r.replycom_keywords),
            replycom_language: convert(r.replycom_language),
            replycom_context_keywords: convert(r.replycom_context_keywords),
            replycom_speakers: convert(r.replycom_speakers),
            replycom_locale_title: r.replycom_locale_title,
            replycom_title: r.replycom_title,
            replycom_topic: convert(r.replycom_topic),
            replycom_start_datetime: r.replycom_start_datetime
        }
    };
}

export function flattenMetadata<S extends DefaultSchema<string[]> | CommonSchema | ReplycomSchema<string[]>>(
        schema: S,
        prefix: MetadataPrefix<S>,
        filter?: Set<CommonMetadataFlatKeys | ReplycomMetadataFlatKeys | DefaultMetadataFlatKeys>
): FlatMetadata<S> {
    const entries = Object.entries(schema) as [keyof S, string[] | string][];
    entries.sort(([k1], [k2]) => (k1 as string).localeCompare(k2 as string));
    const flattened = entries.map(([k, v]): [keyof FlatMetadata<S>, string] => [
            (prefix ? `${prefix}.${String(k)}` : k) as keyof FlatMetadata<S>,
            typeof v === 'string' ? v : v?.join?.(',')
        ]).filter(([k]) => !filter || filter.has(k as any));
    return Object.fromEntries<string>(flattened) as FlatMetadata<S>;
}


export interface TaxonomyTermsDifference<T = string> {
    added: T[];
    removed: T[];
}

export interface GetTaxonomyTermsParameters {
    language?: string;
    start?: number;
    limit: number; // max value: 400
}

export interface TaxonomyTerm {
    id: string;
    master_description: string;
    description: string;
    level: number;
}

export interface TermData {
    id?: string;
    value: string;
}

export interface TaxonomyTerms {
    name: string;
    count: number;
    items: TaxonomyTerm[];
}


export type RequestStatus = 'ERROR' | 'QUEUED' | 'DONE';

export interface GetRequestsParams {
    status?: RequestStatus;
}

export interface MinisitePayload {
    properties: {
        _type: string,
        _subtype: "MiniSite",
        _id: string
    },
    metadata: {
        title: string,
        channel_global_id: string,
        folder_global_id: string,
        rest_user_id: number,
        rest_role_id: string
    }
}

export interface RequestData {
    action: string;
    payload: { properties: Partial<EntityProperties> } | MinisitePayload;
}

export interface Request {
    request_id?: RequestId;
    last_try_datetime: string; // 'yyyy-MM-dd hh:mm:ss.SSS'
    status: RequestStatus;
    request: RequestData;
    _action?: Action; // internal
    siteKey?: SiteKey;
}

export interface Requests {
    results: Request[];
}

export type RenditionsStatus = 'READY' | 'QUEUED' | 'ERROR' | 'EMPTY_' | 'UPLOADING' | 'ENCODING';

export interface RenditionsStatusOutput {
    status: RenditionsStatus;
}

export interface CreateRenditionRequestBody {
    properties?: {
        _file_name: string
    };
    label: string;
    mediaRoleName: MediaRole;
    url: string;
}

export interface EditRenditionRequestBody {
    mediaRoleName: MediaRole;
    url: string;
}

export interface DeleteRenditionParameters {
    label: string;
    mediaRoleName?: MediaRole;
    ignore_if_not_exists?: boolean;
}

export type Action =
    | 'create'
    | 'create_minisite'
    | 'update_metadata'
    | 'edit_cover'
    | 'add_terms'
    | 'delete_terms'
    | 'resync-status'
    | 'version'
    | 'create_friendly_url'
    | 'update_friendly_urls'
    ;

export const DAM_REQUEST_PREFIX: string = 'dam-requests';


export interface Permalink {
    path: string;
    url: string;
}

export interface GetPermalinksOutput {
    permalinks: Record<MediaRole, Permalink>;
}

export interface CreatePermalinksRequestBody {
    title: string;
}


export interface VersionEntityRequestBody {
    properties?: {
        _file_name?: string,
        _revision_tag: string,
    };
    url: string;
}

export interface GetFriendlyUrlOutput {
    'Friendly-URLs': Record<MediaRole, Permalink[]>;
}

export interface CreateFriendlyUrlRequestBody {
    label: string;
    'media-role': MediaRole;
}

export interface CreateMinisiteRequestBody {
    name: string;
}